import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { BaseService } from '../../../http/BaseHttpService';
import { mapNexusAgentDtoToModel } from '../mappers/NexusAgentMapper';
import { NexusCreateAgentRequestDto } from '../dtos/requests/NexusCreateAgentRequestDto';
import { NexusUpdateAgentRequestDto } from '../dtos/requests/NexusUpdateAgentRequestDto';
import { NexusAgentDto } from '../dtos/responses/NexusAgentDto';
import { NexusAgentsResponseDto } from '../dtos/responses/NexusAgentsResponseDto';
import { NexusAgentsLoadResult } from '../dtos/responses/NexusAgentsLoadResult';
import { buildNexusUrl } from '../../../../features/utils/buildNexusUrl';

export class NexusAgentsService extends BaseService {
    private readonly subscriptionKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
    private readonly subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';
    private readonly defaultProviderName = 'Foundry';

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async getAgentsAsync(accessToken: string, includeInactive = false): Promise<NexusAgentsLoadResult> {
        const searchParams = new URLSearchParams({
            includeInactive: includeInactive.toString(),
        });

        const response = await this.getResponseAsync<NexusAgentsResponseDto>(
            {
                url: buildNexusUrl(this.baseUrl, `agents?${searchParams.toString()}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        // maybe change this to not return count and only the ones that are active
        return {
            count: response.count,
            includeInactive: response.includeInactive,
            agents: response.agents.map(mapNexusAgentDtoToModel),
        };
    }

    public async getAgentAsync(agentId: string, accessToken: string): Promise<NexusAgent> {
        const response = await this.getResponseAsync<NexusAgentDto>(
            {
                url: buildNexusUrl(this.baseUrl, `agents/${agentId}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusAgentDtoToModel(response);
    }

    public async getAgentsByProviderAsync(providerName: string, accessToken: string): Promise<NexusAgent[]> {
        const response = await this.getResponseAsync<NexusAgentDto[]>(
            {
                url: buildNexusUrl(this.baseUrl, `agents/provider/${encodeURIComponent(providerName)}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return response.map(mapNexusAgentDtoToModel);
    }

    public async searchAgentsAsync(
        accessToken: string,
        options: {
            searchTerm?: string;
            provider?: string;
            tags?: string;
        },
    ): Promise<NexusAgent[]> {
        const searchParams = new URLSearchParams();

        if (options.searchTerm) {
            searchParams.set('searchTerm', options.searchTerm);
        }

        if (options.provider) {
            searchParams.set('provider', options.provider);
        }

        if (options.tags) {
            searchParams.set('tags', options.tags);
        }

        const query = searchParams.toString();

        const response = await this.getResponseAsync<NexusAgentDto[]>(
            {
                url: buildNexusUrl(this.baseUrl, `agents/search${query ? `?${query}` : ''}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return response.map(mapNexusAgentDtoToModel);
    }

    public async createAgentAsync(
        request: NexusCreateAgentRequestDto,
        accessToken: string,
        providerName = this.defaultProviderName,
    ): Promise<NexusAgent> {
        const formData = new FormData();
        formData.append('Name', request.name);
        formData.append('Instructions', request.instructions);

        if (request.description) {
            formData.append('Description', request.description);
        }

        if (request.model) {
            formData.append('Model', request.model);
        }

        if (request.functionTools) {
            formData.append('FunctionTools', JSON.stringify(request.functionTools));
        }

        request.filesForFileSearch?.forEach((file) => formData.append('FilesForFileSearch', file));
        request.filesForCodeInterpreter?.forEach((file) => formData.append('FilesForCodeInterpreter', file));

        if (request.enableCodeInterpreter !== undefined) {
            formData.append('EnableCodeInterpreter', request.enableCodeInterpreter.toString());
        }

        if (request.enableFileSearch !== undefined) {
            formData.append('EnableFileSearch', request.enableFileSearch.toString());
        }

        request.mcpServerIds?.forEach((id) => formData.append('McpServerIds', id));

        if (request.metadata && Object.keys(request.metadata).length > 0) {
            formData.append('Metadata', JSON.stringify(request.metadata));
        }

        if (request.agentIconFile) {
            formData.append('AgentIconFile', request.agentIconFile);
        }

        const searchParams = new URLSearchParams({
            providerName,
        });

        const response = await this.getResponseAsync<NexusAgentDto>(
            {
                url: buildNexusUrl(this.baseUrl, `agents/create?${searchParams.toString()}`),
                method: 'POST',
                body: formData,
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusAgentDtoToModel(response);
    }

    public async updateAgentAsync(
        agentId: string,
        request: NexusUpdateAgentRequestDto,
        accessToken: string,
        providerName = this.defaultProviderName,
    ): Promise<NexusAgent> {
        const formData = new FormData();

        if (request.instructions) {
            formData.append('Instructions', request.instructions);
        }

        if (request.description) {
            formData.append('Description', request.description);
        }

        if (request.model) {
            formData.append('Model', request.model);
        }

        if (request.functionTools !== undefined) {
            formData.append('functionTools', JSON.stringify(request.functionTools));
        }

        if (request.filesForFileSearch && request.filesForFileSearch.length > 0) {
            for (const file of request.filesForFileSearch) {
                formData.append('filesForFileSearch', file);
            }
        }

        if (request.filesForCodeInterpreter && request.filesForCodeInterpreter.length > 0) {
            for (const file of request.filesForCodeInterpreter) {
                formData.append('filesForCodeInterpreter', file);
            }
        }

        if (request.filesToDeleteForCodeInterpreter && request.filesToDeleteForCodeInterpreter.length > 0) {
            for (const fileName of request.filesToDeleteForCodeInterpreter) {
                formData.append('filesToDeleteForCodeInterpreter', fileName);
            }
        }

        if (request.filesToDeleteForFileSearch && request.filesToDeleteForFileSearch.length > 0) {
            for (const fileName of request.filesToDeleteForFileSearch) {
                formData.append('filesToDeleteForFileSearch', fileName);
            }
        }

        if (request.enableCodeInterpreter !== undefined && request.enableCodeInterpreter !== null) {
            formData.append('enableCodeInterpreter', String(request.enableCodeInterpreter));
        }

        if (request.enableFileSearch !== undefined && request.enableFileSearch !== null) {
            formData.append('enableFileSearch', String(request.enableFileSearch));
        }

        if (request.mcpServerIds !== undefined && request.mcpServerIds !== null) {
            request.mcpServerIds.forEach((id) => formData.append('McpServerIds', id));
        }

        if (request.metadata !== undefined && request.metadata !== null) {
            formData.append('metadata', JSON.stringify(request.metadata));
        }

        if (request.agentIconFile) {
            formData.append('AgentIconFile', request.agentIconFile);
        }

        const searchParams = new URLSearchParams({
            providerName,
        });

        const response = await this.getResponseAsync<NexusAgentDto>(
            {
                url: buildNexusUrl(this.baseUrl, `agents/${agentId}?${searchParams.toString()}`),
                method: 'PATCH',
                body: formData,
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusAgentDtoToModel(response);
    }

    // soft deletion
    public async deleteAgentAsync(agentId: string, accessToken: string): Promise<void> {
        await this.getResponseAsync(
            {
                url: buildNexusUrl(this.baseUrl, `agents/${agentId}`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }

    public async hardDeleteAgentAsync(
        agentId: string,
        accessToken: string,
        providerName = this.defaultProviderName,
    ): Promise<void> {
        const searchParams = new URLSearchParams({
            providerName,
        });

        await this.getResponseAsync(
            {
                url: buildNexusUrl(this.baseUrl, `agents/${agentId}/hard?${searchParams.toString()}`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }
}
